import { useEffect, useRef, useState } from 'react';
import { configureKatalLogger, entryLog, logger } from '../utils/logger';
import { configureKatalMetrics, trackPerformance, publishCounter, getMetricsUserEnv } from '../utils/metrics';
import { RumClient } from '../services/rum/CloudWatchRumService';
import type { LoggedInUserInfo } from '../types/auth/auth.types';

interface UseObservabilityInitProps {
  user: LoggedInUserInfo | null;
  isAuthInitialized: boolean;
  isMembershipCheckComplete: boolean;
}

interface UseObservabilityInitReturn {
  metricsReady: boolean;
}

/**
 * Custom hook to initialize observability tools (logger and metrics) after authentication
 *
 * This hook:
 * 1. Initializes KatalLogger with user context
 * 2. Initializes KatalMetrics with user context
 * 3. Logs successful authentication
 * 4. Only runs once per user session
 * 5. Returns metricsReady flag for dependent hooks
 *
 * @param user - The authenticated user information
 * @param isAuthInitialized - Whether authentication is complete
 * @param isMembershipCheckComplete - Whether membership check is complete
 * @returns Object with metricsReady flag
 */
export const useObservabilityInit = ({
  user,
  isAuthInitialized,
  isMembershipCheckComplete,
}: UseObservabilityInitProps): UseObservabilityInitReturn => {
  const hasInitialized = useRef(false);
  const [metricsReady, setMetricsReady] = useState(false);

  useEffect(() => {
    // Only initialize once when we have a user and all checks are complete
    if (!user || !isAuthInitialized || !isMembershipCheckComplete || hasInitialized.current) {
      return;
    }

    // Mark as initialized to prevent duplicate initialization
    hasInitialized.current = true;

    try {
      // Initialize logger
      configureKatalLogger(user.userAlias, user.displayName);

      // Initialize metrics
      configureKatalMetrics(user.userAlias, user.displayName);

      // Initialize CloudWatch RUM
      RumClient.initialize(user.userAlias);

      // Log successful authentication (async but don't wait)
      void entryLog();

      // Track initial app load time
      // Measure from page load start (marked in index.html) to app ready (now)
      if (window.performance) {
        try {
          // Mark when app is ready
          window.performance.mark('verity-app-ready');

          // Measure from page load start to app ready
          const measure = window.performance.measure(
            'verity-initial-app-load',
            'verity-page-load-start',
            'verity-app-ready'
          );

          if (measure && measure.duration) {
            // Track the initial app load time
            trackPerformance('InitialAppLoad', measure.duration, {
              userAlias: user.userAlias,
              stage: 'alpha',
            });

            // Also log for debugging
            logger.info(`Initial app load completed in ${Math.round(measure.duration)}ms`, {
              durationMs: measure.duration,
            });
          }
        } catch (perfError) {
          // Performance API not available or marks not found - not critical
          console.warn('Could not measure initial app load time:', perfError);
        }
      }

      // Track user login — published once per session on successful auth
      publishCounter('UserLogin', 1, undefined, getMetricsUserEnv());

      // Mark metrics as ready
      setMetricsReady(true);
    } catch (error) {
      // Log to console as fallback since logger may not be initialized yet
      console.error('Failed to initialize observability tools:', error);
      setMetricsReady(false);
    }
  }, [user, isAuthInitialized, isMembershipCheckComplete]);

  // Reset initialization flag when user changes
  useEffect(() => {
    if (!user) {
      hasInitialized.current = false;
      setMetricsReady(false);
    }
  }, [user?.userAlias]); // eslint-disable-line react-hooks/exhaustive-deps

  return { metricsReady };
};
